import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarClock, XCircle, Loader2, ArrowLeft, Check } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SlotPicker } from "@/components/booking/SlotPicker";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const detail = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const Shell = ({ children }) => (
  <div className="relative min-h-screen bg-ink text-white">
    <div className="noise-overlay" aria-hidden="true" />
    <header className="border-b border-white/10">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="manage-logo-home" aria-label="AiCendor home"><Logo /></Link>
        <Link to="/" data-testid="manage-back-home" className="link-underline flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300 hover:text-white">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
      </div>
    </header>
    <main className="mx-auto max-w-4xl px-6 py-20 lg:px-10 lg:py-28">{children}</main>
    <Footer />
  </div>
);

const StatusPill = ({ status }) => (
  <span
    data-testid="manage-status"
    className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] ${
      status === "confirmed" ? "border-gold/60 text-gold" : "border-slate-600 text-slate-400"
    }`}
  >
    {status}
  </span>
);

export default function ManageBooking() {
  const { token } = useParams();
  const [booking, setBooking] = useState();
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("view"); // view | reschedule | cancel
  const [date, setDate] = useState();
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/bookings/manage/${token}`)
      .then((r) => setBooking(r.data))
      .catch((e) => setError(detail(e, "We couldn't find that booking.")));
  }, [token]);

  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/manage/${token}/cancel`);
      setBooking(data);
      setMode("view");
      toast.success("Your booking has been cancelled.");
    } catch (e) {
      toast.error(detail(e, "Couldn't cancel — please try again."));
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/manage/${token}/reschedule`, { slot_start: slot.start });
      setBooking(data);
      setMode("view");
      setDate(undefined);
      setSlot(null);
      toast.success("Your booking has been moved. A new confirmation is on its way.");
    } catch (e) {
      toast.error(detail(e, "Couldn't reschedule — please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <Shell>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Manage booking</p>
        <h1 className="mt-6 font-display text-3xl sm:text-4xl" data-testid="manage-error">{error}</h1>
        <p className="mt-6 text-slate-400">The link may have expired or been mistyped. You can always book a new time.</p>
        <Link to="/#book" data-testid="manage-book-new" className="btn-sweep btn-sweep-light mt-10 inline-block border border-accent bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-ink">
          Book a consultation
        </Link>
      </Shell>
    );
  }
  if (!booking) {
    return (
      <Shell>
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Manage booking</p>
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <h1 className="font-display text-3xl tracking-tight sm:text-4xl" data-testid="manage-when">
          {booking.when.replace(` (${booking.timezone})`, "")}
        </h1>
        <StatusPill status={booking.status} />
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">UK time · {booking.timezone} · 30 minutes</p>

      <dl className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3">
        {[["Booked for", booking.name], ["Email", booking.email], ["Topic", booking.topic]].map(([k, v]) => (
          <div key={k}>
            <dt className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{k}</dt>
            <dd className="mt-2 text-sm text-slate-200 sm:text-base">{v}</dd>
          </div>
        ))}
      </dl>

      {booking.status === "cancelled" && (
        <div className="mt-12 border border-white/10 p-6" data-testid="manage-cancelled-notice">
          <p className="text-sm text-slate-300">This booking has been cancelled. If you&rsquo;d still like to talk, you&rsquo;re welcome to book a new time.</p>
          <Link to="/#book" data-testid="manage-book-again" className="btn-sweep mt-6 inline-block border border-white/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white">
            Book a new time
          </Link>
        </div>
      )}

      {booking.can_manage && mode === "view" && (
        <div className="mt-12 flex flex-col items-start gap-4 sm:flex-row">
          <button type="button" data-testid="manage-reschedule-button" onClick={() => setMode("reschedule")} className="btn-sweep btn-sweep-light flex items-center gap-3 border border-accent bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-ink">
            <CalendarClock className="h-4 w-4" /> Reschedule
          </button>
          <button type="button" data-testid="manage-cancel-button" onClick={() => setMode("cancel")} className="btn-sweep flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white">
            <XCircle className="h-4 w-4" /> Cancel booking
          </button>
        </div>
      )}

      {mode === "cancel" && (
        <div className="mt-12 glass-panel p-6 sm:p-8" data-testid="manage-cancel-confirm">
          <p className="font-display text-xl">Cancel this consultation?</p>
          <p className="mt-3 text-sm text-slate-400">Your slot will be released immediately. You can book again at any time.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button type="button" data-testid="manage-cancel-yes" disabled={busy} onClick={cancel} className="btn-sweep btn-sweep-light flex items-center gap-2 border border-accent bg-accent px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Yes, cancel it
            </button>
            <button type="button" data-testid="manage-cancel-no" onClick={() => setMode("view")} className="link-underline font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 hover:text-white">
              Keep my booking
            </button>
          </div>
        </div>
      )}

      {mode === "reschedule" && (
        <div className="mt-12 glass-panel p-6 sm:p-8" data-testid="manage-reschedule-panel">
          <p className="font-display text-xl">Choose a new time</p>
          <div className="mt-6">
            <SlotPicker date={date} onDateChange={setDate} slot={slot} onSelect={setSlot} excludeBookingId={booking.id} />
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-white/10 pt-8">
            <button type="button" data-testid="manage-reschedule-confirm" disabled={!slot || busy} onClick={reschedule} className="btn-sweep btn-sweep-light flex items-center gap-2 border border-accent bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-ink">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {slot && date ? `Move to ${format(date, "EEE d MMM")} ${slot.label}` : "Select a new slot"}
            </button>
            <button type="button" data-testid="manage-reschedule-back" onClick={() => setMode("view")} className="link-underline font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 hover:text-white">
              Back
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
}
